import { assert, assertNever, BaseNumber, CheckedAddress, NormalizedNumber, raise } from '@sparkdotfi/common-universal'
import BigNumber from 'bignumber.js'
import { decodeEventLog, erc4626Abi, TransactionReceipt } from 'viem'
import { gnosis } from 'viem/chains'

import { WithdrawFromWethAction } from '../../withdraw-from-weth/types'
import { WithdrawFromSavingsAction, WithdrawFromSavingsObjective } from '../types'
import { formatMaxAmountInForPsm3 } from './formatMaxAmountInForPsm3'
import { getSavingsWithdrawActionPath } from './getSavingsWithdrawActionPath'
import {
  migrationActionsConfig,
  psm3Address,
  savingsXDaiAdapterAddress,
  usdsPsmActionsConfig,
} from '@/config/contracts-generated'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { getApproveActions } from '@/features/actions/logic/getApproveActions'
import { Action, ActionContext } from '@/features/actions/logic/types'
import { SavingsConverter } from '@/features/savings/logic/savings-converters/types'

export function createWithdrawFromSavingsActions(
  objective: WithdrawFromSavingsObjective,
  context: ActionContext,
): Action[] {
  const chainId = context.chainId

  assert(context.savingsAccounts, 'Savings account repository info is required for withdraw from savings action')
  const withdrawAction: WithdrawFromSavingsAction = {
    type: 'withdrawFromSavings',
    amount: objective.amount,
    token: objective.token,
    savingsToken: objective.savingsToken,
    underlyingToken: context.savingsAccounts.findOneBySavingsToken(objective.savingsToken).underlyingToken,
    isRedeem: objective.isRedeem,
    mode: objective.mode,
    receiver: objective.mode === 'send' ? objective.receiver : undefined,
    displayMode: objective.displayMode,
  }

  const getSusdsApproveActions = createApproveActionsFromSavingsConverter({
    objective,
    converter: context.savingsAccounts?.findBySavingsTokenSymbol(TokenSymbol('sUSDS'))?.converter,
    context,
  })
  const getSdaiApproveActions = createApproveActionsFromSavingsConverter({
    objective,
    converter: context.savingsAccounts?.findBySavingsTokenSymbol(TokenSymbol('sDAI'))?.converter,
    context,
  })

  const actionPath = getSavingsWithdrawActionPath({
    token: objective.token,
    savingsToken: objective.savingsToken,
    chainId,
  })

  switch (actionPath) {
    case 'spusdc-to-usdc':
    case 'spusdt-to-usdt':
    case 'speth-to-weth':
    case 'sppyusd-to-pyusd':
    case 'susds-to-usds':
    case 'sdai-to-dai':
    case 'susdc-to-usdc':
    case 'stusds-to-usds':
    case 'base-susdc-to-usdc':
    case 'arbitrum-susdc-to-usdc':
    case 'optimism-susdc-to-usdc':
    case 'unichain-susdc-to-usdc':
    case 'avalanche-spusdc-to-usdc':
      return [withdrawAction]

    case 'speth-to-eth':
      return createEthWithdrawFromSpethActions({ objective, context, withdrawAction })

    case 'susds-to-usdc':
      return [...getSusdsApproveActions(getContractAddress(usdsPsmActionsConfig.address, chainId)), withdrawAction]

    case 'sdai-to-usds':
      return [...getSdaiApproveActions(getContractAddress(migrationActionsConfig.address, chainId)), withdrawAction]

    case 'sdai-to-sexy-dai':
      return [...getSdaiApproveActions(savingsXDaiAdapterAddress[gnosis.id]), withdrawAction]

    case 'base-susds-to-usdc':
    case 'base-susds-to-usds':
    case 'arbitrum-susds-to-usds':
    case 'arbitrum-susds-to-usdc':
    case 'optimism-susds-to-usds':
    case 'optimism-susds-to-usdc':
    case 'unichain-susds-to-usds':
    case 'unichain-susds-to-usdc': {
      assert(
        context.savingsAccounts,
        'Savings accounts repository info is required for psm withdraw from savings action',
      )
      const { converter } = context.savingsAccounts.findOneBySavingsTokenSymbol(TokenSymbol('sUSDS'))

      const maxSharesAmount = converter.convertToShares({ assets: objective.amount })
      const maxAmountIn = BaseNumber(
        formatMaxAmountInForPsm3({
          susds: objective.savingsToken,
          susdsAmount: maxSharesAmount,
          assetOut: objective.token,
        }),
      )
      const approveActions = getApproveActions({
        token: objective.savingsToken,
        spender: getContractAddress(psm3Address, chainId),
        value: objective.isRedeem ? objective.amount : objective.savingsToken.fromBaseUnit(maxAmountIn),
        context,
      })
      return [...approveActions, withdrawAction]
    }

    default:
      assertNever(actionPath)
  }
}

interface CreateApproveActionFromSavingsConverterParams {
  objective: WithdrawFromSavingsObjective
  converter?: SavingsConverter
  context: ActionContext
}

function createApproveActionsFromSavingsConverter({
  objective,
  converter,
  context,
}: CreateApproveActionFromSavingsConverterParams) {
  return (spender: CheckedAddress): Action[] => {
    // @note This assert is here and not on the callsite
    // to prevent raising an error while only one of the savings info is provided
    assert(converter, 'Savings info is required for withdraw from savings action approval')
    const savingTokenApprovalAmountEstimate = converter.convertToShares({ assets: objective.amount })

    return getApproveActions({
      token: objective.savingsToken,
      spender,
      value: objective.isRedeem
        ? objective.amount
        : NormalizedNumber(
            savingTokenApprovalAmountEstimate.toFixed(objective.savingsToken.decimals, BigNumber.ROUND_UP),
          ),
      context,
    })
  }
}

interface CreateEthWithdrawFromSpethActionsParams {
  objective: WithdrawFromSavingsObjective
  context: ActionContext
  withdrawAction: WithdrawFromSavingsAction
}

function createEthWithdrawFromSpethActions({
  objective,
  context,
  withdrawAction,
}: CreateEthWithdrawFromSpethActionsParams): Action[] {
  const spethSavingsAccount =
    context.savingsAccounts?.findBySavingsTokenSymbol(TokenSymbol('spETH')) ??
    raise('spETH savings account is required for withdraw ETH from spETH action')

  const fallbackWethWithdrawalAmount = (() => {
    if (objective.isRedeem) {
      return spethSavingsAccount.converter.convertToAssets({ shares: objective.amount })
    }
    return objective.amount
  })()

  const wethWithdrawalAmount = (() => {
    const [, withdrawReceipt] = context.txReceipts.find(([action]) => action.type === 'withdrawFromSavings') ?? []
    return withdrawReceipt
      ? getWethWithdrawalAmountFromWithdrawReceipt(objective.token, withdrawReceipt)
      : fallbackWethWithdrawalAmount
  })()

  const withdrawFromWethAction: WithdrawFromWethAction = {
    type: 'withdrawFromWeth',
    eth: objective.token,
    amount: wethWithdrawalAmount,
  }

  return [
    {
      ...withdrawAction,
      token: spethSavingsAccount.underlyingToken, // spETH underlying token is WETH
    },
    withdrawFromWethAction,
  ]
}

function getWethWithdrawalAmountFromWithdrawReceipt(token: Token, receipt: TransactionReceipt): NormalizedNumber {
  for (const log of receipt.logs) {
    try {
      const decodedLog = decodeEventLog({
        abi: erc4626Abi,
        data: log.data,
        topics: log.topics,
      })
      if (decodedLog.eventName === 'Withdraw') {
        return token.fromBaseUnit(BaseNumber(decodedLog.args.assets))
      }
    } catch {
      // ignore error - there may be some other events we are not able to parse
    }
  }
  throw new Error('Withdraw receipt does not contain withdraw event')
}
